import { useNavigate } from "react-router-dom";
import { FileText, ClipboardList, Calculator, TrendingUp, FolderOpen, MessageSquareText, ArrowRight, Briefcase, Building2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { ADVISOR_SEATED, OFFICE_IMG } from "../lib/constants";

const services = [
  { icon: FileText, title: "Déclaration fiscale", desc: "Préparation et dépôt de la déclaration fiscale de votre activité ou société, dans le respect des délais cantonaux." },
  { icon: ClipboardList, title: "Suivi administratif fiscal", desc: "Gestion des correspondances avec les administrations, acomptes, délais et taxations." },
  { icon: Calculator, title: "Accompagnement comptable de base", desc: "Tenue simplifiée, classement des pièces et préparation des documents comptables nécessaires." },
  { icon: TrendingUp, title: "Optimisation", desc: "Analyse des leviers légaux d'optimisation adaptés à votre structure et votre canton." },
  { icon: FolderOpen, title: "Préparation des documents", desc: "Constitution des dossiers, justificatifs et annexes exigés par les autorités fiscales suisses." },
  { icon: MessageSquareText, title: "Conseil personnalisé", desc: "Un interlocuteur dédié qui connaît votre activité et répond à vos questions toute l'année." },
];

export default function Entreprises() {
  const navigate = useNavigate();

  return (
    <main data-testid="entreprises-page" className="pt-[76px]">
      <SEO
        title="Fiscalité indépendant et entreprise Suisse | Fiscoconseils"
        description="Fiscalité indépendant Suisse et fiscalité entreprise Suisse : déclaration fiscale, suivi administratif, accompagnement comptable de base, optimisation et conseil personnalisé."
      />

      <section className="relative bg-[#0A1128] overflow-hidden">
        <img src={OFFICE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-4">Indépendants & Entreprises — Suisse uniquement</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white max-w-3xl leading-[1.05]">
            Votre partenaire fiscal en Suisse
          </h1>
          <p className="text-base md:text-lg text-[#D1D5DB] mt-6 max-w-2xl leading-relaxed">
            Fiscoconseils accompagne exclusivement les indépendants et entreprises basés en Suisse : déclarations, suivi administratif, comptabilité de base et optimisation fiscale.
          </p>
          <div className="flex flex-wrap gap-4 mt-10">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[#C4C8CC] border border-white/20 rounded-full px-5 py-2.5"><Briefcase size={15} strokeWidth={1.75} /> Indépendants</span>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[#C4C8CC] border border-white/20 rounded-full px-5 py-2.5"><Building2 size={15} strokeWidth={1.75} /> Sàrl · SA · Raison individuelle</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div
              key={s.title}
              data-testid={`entreprise-service-${i}`}
              className="bg-white border border-[#C4C8CC]/50 rounded-2xl p-8 shadow-[0_8px_32px_rgba(10,17,40,0.06)] hover:-translate-y-1 transition-transform duration-300"
            >
              <div className="w-12 h-12 rounded-full bg-[#F8F9FA] border border-[#E5E7EB] flex items-center justify-center">
                <s.icon size={22} strokeWidth={1.5} className="text-[#0A1128]" />
              </div>
              <h2 className="text-lg font-bold text-[#0A1128] mt-6">{s.title}</h2>
              <p className="text-sm text-[#6B7280] mt-3 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FA] border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <img src={ADVISOR_SEATED} alt="Conseiller fiscal entreprises Fiscoconseils" loading="lazy" width="640" height="400" className="rounded-2xl object-cover w-full h-[400px] shadow-[0_8px_32px_rgba(10,17,40,0.12)]" />
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128]">
              Concentrez-vous sur votre activité, nous gérons votre fiscalité
            </h2>
            <p className="text-base text-[#4B5563] mt-6 leading-relaxed">
              Que vous soyez indépendant en raison individuelle ou dirigeant d'une société suisse, nous adaptons notre accompagnement à votre chiffre d'affaires, votre canton et vos obligations TVA.
            </p>
            <button
              data-testid="entreprises-devis-btn"
              onClick={() => navigate("/devis")}
              className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#0A1128] rounded-full px-8 py-4 hover:bg-[#1A2342] transition-colors duration-200"
            >
              Demander un devis entreprise <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
